import {
  XBOT_DATA_PAGE_VERSION, XBOT_MESSAGE_VERSION, XbotChatRequest, XbotDataPage, XbotDataPageError,
  XbotDataPageErrorCode, XbotDataPageRequest, XbotErrorBody, XbotMessage, XbotPageContext, XbotStarterExample, XbotStarters,
  XbotStreamEvent,
} from "@dto/pyxbot-agent.dto";
import { XbotLoadPage } from "./host";
import { applyEvent, endOfStream, settledMessage, XbotTurn } from "./turnState";
import { API_URL } from "src/data/Api";
import { ABORTED, askXbot, getJson, isObject, messageOf, postJson, readJson, textOf, XbotAborted, xbotFetch, xbotServerPath } from "../xbotFetch";
import { Label } from "src/data/Label";

// Talks to NG's own /api/pyxbot/v1 chat/starters/data/thread-close routes, all under the person's login.
// Suggestion-editor routes live separately in ../suggestions/client.ts. Events are folded into a message by turnState.

export const XBOT_CHAT_BASE: string = API_URL.XBOT;

export const XBOT_STREAM_ACCEPT = "text/event-stream, application/json";

/** The service, or NG on its behalf, would not take the request: its error, or one of the chat's own. */
export interface XbotRefused {
  kind: "refused";
  status: number;
  error: XbotErrorBody;
}

export function refused(status: number, code: string, message: string): XbotRefused {
  return { kind: "refused", status, error: { code, message } };
}

/** `turn` is set only when the chat followed the turn via onTurn. */
export type XbotChatOutcome = { kind: "message"; message: XbotMessage; turn?: XbotTurn } | XbotRefused | XbotAborted;

export type XbotOnTurn = (turn: XbotTurn) => void;

export const XBOT_CHAT_ERRORS = {
  unreachable: `${Label.XORBOT} is not available right now. Ask an administrator to start the ${Label.XORBOT} service.`,
  unreadable: `${Label.XORBOT} answered with something the chat cannot show. Try again.`,
  signedOut: "Your session has ended. Sign in again to keep asking.",
};

// The refusal body, when the payload has one; `detail` is passed through as-is when present.
export function errorBodyOf(value: unknown): XbotErrorBody | null {
  if (!isObject(value) || !isObject(value.error)) {
    return null;
  }
  const { code, message, detail } = value.error;
  if (typeof code !== "string" || typeof message !== "string") {
    return null;
  }
  return detail === undefined || detail === null ? { code, message } : { code, message, detail };
}

// Never rejects: network/parse failures come back as a "refused" outcome too. With `onTurn`, follows the
// turn's SSE events and resolves with the message it settled on (see settledMessage) instead of one plain reply.
export async function sendXbotChat(body: XbotChatRequest, signal?: AbortSignal, onTurn?: XbotOnTurn): Promise<XbotChatOutcome> {
  const response = await askXbot(`${XBOT_CHAT_BASE}/chat`, postJson(body, signal, onTurn ? XBOT_STREAM_ACCEPT : undefined));
  if (onTurn && response && isEventStream(response)) {
    return readTurn(response, onTurn, signal);
  }
  const payload = response && await readJson(response);
  if (signal?.aborted) {
    return ABORTED;
  }
  if (!response) {
    return refused(0, "unavailable", XBOT_CHAT_ERRORS.unreachable);
  }
  if (response.ok && isObject(payload) && payload.schema_version === XBOT_MESSAGE_VERSION) {
    return { kind: "message", message: payload as unknown as XbotMessage };
  }
  const refusal = errorBodyOf(payload);
  if (refusal) {
    return { kind: "refused", status: response.status, error: refusal };
  }
  if (response.status === 401) {
    return refused(401, "unauthorized", XBOT_CHAT_ERRORS.signedOut);
  }
  // NG's own error pages carry a plain `message` rather than the agent's error object.
  return refused(response.status, response.ok ? "bad_response" : "http_error", messageOf(payload) || XBOT_CHAT_ERRORS.unreadable);
}

function isEventStream(response: Response): boolean {
  const type = (response.headers?.get("content-type") || "").toLowerCase();
  return response.ok && Boolean(response.body) && type.startsWith("text/event-stream");
}

/** One server-sent event: its name and its data, or null for a comment or a frame without data. */
export function parseEventFrame(frame: string): { event: string; data: string } | null {
  let event = "message";
  const data: string[] = [];
  for (const raw of frame.split("\n")) {
    const line = raw.endsWith("\r") ? raw.slice(0, -1) : raw;
    if (!line || line.startsWith(":")) {
      continue;
    }
    const colon = line.indexOf(":");
    const field = colon < 0 ? line : line.slice(0, colon);
    const rest = colon < 0 ? "" : line.slice(colon + 1);
    const value = rest.startsWith(" ") ? rest.slice(1) : rest;
    if (field === "event") {
      event = value;
    } else if (field === "data") {
      data.push(value);
    }
  }
  return data.length ? { event, data: data.join("\n") } : null;
}

/** An event of a turn, or null for anything that is not one (its name and its own tag must agree). */
function streamEventOf(frame: string): XbotStreamEvent | null {
  const parsed = parseEventFrame(frame);
  if (!parsed) {
    return null;
  }
  let payload: unknown;
  try {
    payload = JSON.parse(parsed.data);
  } catch {
    return null;
  }
  if (!isObject(payload) || payload.event !== parsed.event || typeof payload.seq !== "number") {
    return null;
  }
  return payload as unknown as XbotStreamEvent;
}

// Stream ending without the turn's final event means it didn't finish; never having an accepted event
// at all means the answer can't be shown.
async function readTurn(response: Response, onTurn: XbotOnTurn, signal?: AbortSignal): Promise<XbotChatOutcome> {
  const reader = (response.body as ReadableStream<Uint8Array>).getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let turn: XbotTurn | null = null;
  try {
    for (;;) {
      const chunk = await reader.read();
      if (chunk.done) {
        break;
      }
      buffer = (buffer + decoder.decode(chunk.value, { stream: true })).split("\r\n").join("\n");
      let end = buffer.indexOf("\n\n");
      while (end >= 0) {
        const event = streamEventOf(buffer.slice(0, end));
        buffer = buffer.slice(end + 2);
        end = buffer.indexOf("\n\n");
        const next: XbotTurn | null = event ? applyEvent(turn, event) : turn;
        if (next && next !== turn) {
          turn = next;
          onTurn(next);
        }
      }
    }
  } catch {
    // A connection that broke off ends the stream here; what was folded so far says how far the turn got.
  }
  if (signal?.aborted) {
    return ABORTED;
  }
  const ended = endOfStream(turn);
  const message = settledMessage(ended);
  if (!ended || !message) {
    return refused(response.status, "bad_response", XBOT_CHAT_ERRORS.unreadable);
  }
  if (ended !== turn) {
    onTurn(ended);
  }
  return { kind: "message", message, turn: ended };
}

/** The codes a data page may be refused with; each has its own sentence in the block. */
const DATA_PAGE_ERROR_CODES: ReadonlySet<string> = new Set<XbotDataPageErrorCode>([
  "unknown_ref", "expired", "purged", "forbidden", "thread_not_found", "malformed_request", "context_invalid", "no_access",
]);

/** The address of one data page; series_index goes along only when a series is named. */
export function dataPageUrl(threadId: string, ref: string, query: XbotDataPageRequest): string {
  const params = new URLSearchParams();
  params.set("offset", String(query.offset));
  params.set("limit", String(query.limit));
  if (typeof query.series_index === "number") {
    params.set("series_index", String(query.series_index));
  }
  return `${XBOT_CHAT_BASE}/data/${encodeURIComponent(threadId)}/${encodeURIComponent(ref)}?${params.toString()}`;
}

// A page, or a refusal with a known data-page code, is returned as-is; anything else rejects so the block can offer a retry.
export const loadXbotPage: XbotLoadPage = async (threadId, ref, query, signal) => {
  const response = await xbotFetch(dataPageUrl(threadId, ref, query), getJson(signal));
  const payload = await readJson(response);
  if (response.ok && isObject(payload) && payload.schema_version === XBOT_DATA_PAGE_VERSION && !("error" in payload)) {
    return payload as unknown as XbotDataPage;
  }
  const refusal = errorBodyOf(payload);
  if (refusal && DATA_PAGE_ERROR_CODES.has(refusal.code)) {
    const refused: XbotDataPageError = {
      schema_version: XBOT_DATA_PAGE_VERSION,
      error: { code: refusal.code as XbotDataPageErrorCode, message: refusal.message },
    };
    return refused;
  }
  throw new Error(refusal ? `The data page was refused (${refusal.code})` : `The data page did not come (HTTP ${response.status})`);
};

const XBOT_STARTERS_VERSION = "agent.starters/1";

function examplesOf(value: unknown): XbotStarterExample[] | null {
  if (!Array.isArray(value)) {
    return null;
  }
  const examples: XbotStarterExample[] = [];
  for (const one of value) {
    if (isObject(one) && typeof one.key === "string" && typeof one.label === "string" && one.label.trim()) {
      examples.push({ key: one.key, label: one.label });
    }
  }
  return examples;
}

// Never rejects: any failure (service down, bad reply, aborted) resolves to null and the window falls
// back to its own built-in examples. The server allocates threadId when none is given.
export async function loadXbotStarters(
  pageContext: XbotPageContext | null | undefined,
  threadId: string | null | undefined,
  signal?: AbortSignal,
): Promise<XbotStarters | null> {
  // a missing thread or page is left out of the JSON: the server allocates the thread
  const body = { thread_id: threadId || undefined, page_context: xbotPageContext(pageContext) ?? undefined };
  const response = await askXbot(`${XBOT_CHAT_BASE}/starters`, postJson(body, signal));
  const payload = response?.ok ? await readJson(response) : null;
  if (signal?.aborted || !isObject(payload) || payload.schema_version !== XBOT_STARTERS_VERSION) {
    return null;
  }
  const pageExamples = examplesOf(payload.page_examples);
  const examples = examplesOf(payload.examples);
  if (!pageExamples || !examples) {
    return null;
  }
  const about = isObject(payload.about) && typeof payload.about.name === "string" && payload.about.name.trim()
    ? payload.about as unknown as XbotStarters["about"]
    : null;
  return { ...(payload as unknown as XbotStarters), about, page_examples: pageExamples, examples };
}

// sendBeacon survives the page closing (an ordinary request wouldn't); falls back to a keepalive fetch.
// Nobody reads the response either way - an unclosed thread key just lapses on its own.
export function closeXbotThread(threadId: string): void {
  if (!threadId) {
    return;
  }
  const url = `${XBOT_CHAT_BASE}/thread/${encodeURIComponent(threadId)}/close`;
  try {
    if (typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function" && navigator.sendBeacon(xbotServerPath(url))) {
      return;
    }
  } catch {
    // A refused beacon falls through to the plain request below.
  }
  void xbotFetch(url, { method: "POST", keepalive: true }).catch(() => undefined);
}

function wholeSeconds(raw: unknown): number | null {
  return typeof raw === "number" && Number.isFinite(raw) ? Math.round(raw) : null;
}

const ITEM_ID_MAX = 128;

// The server rejects unknown fields and non-integer-second window bounds (which would refuse the whole
// question), so only known fields are copied and a custom range is rounded to whole seconds.
export function xbotPageContext(context: XbotPageContext | null | undefined): XbotPageContext | null {
  if (!context) {
    return null;
  }
  const itemId = textOf(context.item_id);
  if (!itemId || itemId.length > ITEM_ID_MAX) {
    return null;
  }
  const page: XbotPageContext = { item_id: itemId };
  for (const key of ["label", "platform", "kind", "class", "page"] as const) {
    const value = textOf(context[key]);
    if (value) {
      page[key] = value;
    }
  }
  const range = context.window;
  if (range) {
    const preset = textOf(range.preset);
    const start = wholeSeconds(range.start);
    const end = wholeSeconds(range.end);
    if (preset) {
      page.window = { preset };
    } else if (start !== null && end !== null && start > 0 && end > start) {
      page.window = { start, end };
    }
  }
  return page;
}
